import { Button, Card, Empty, Modal, Table, Tag, Tooltip } from "antd";
import { ProfilingBarChart } from "components/atoms/ProfilingChart";
import { useState } from "react";

const SummaryCards = ({ totalRecords, totalColumns, lastUpdated }) => {
    const cards = [
        { label: "Total Records", value: totalRecords != null ? Number(totalRecords).toLocaleString() : "-", color: "#1890ff" },
        { label: "Total Columns", value: totalColumns != null ? Number(totalColumns).toLocaleString() : "-", color: "#1890ff" },
        { label: "Last Profiled", value: lastUpdated || "Never", color: lastUpdated ? "#1890ff" : "#999" },
    ];

    return (
        <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
            {cards.map((card) => (
                <Card
                    key={card.label}
                    size="small"
                    style={{ flex: 1, background: "#fafafa", borderColor: "#e8e8e8" }}
                    bodyStyle={{ padding: "8px 12px" }}
                >
                    <div style={{ fontSize: 12, color: "#444", textTransform: "capitalize" }}>{card.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: card.color, marginTop: 2 }}>{card.value}</div>
                </Card>
            ))}
        </div>
    );
};

const getInferredTypeColor = (type) => {
    if (!type) return undefined;
    const colors = {
        ID: "blue",
        PersonName: "green",
        Email: "orange",
        Phone: "cyan",
        Date: "purple",
        Boolean: "geekblue",
        Currency: "gold",
        URL: "lime",
    };
    return colors[type] || "default";
};

const formatRangeOrLength = (col) => {
    if (col.rangeMin != null && col.rangeMax != null) {
        return `${col.rangeMin}-${col.rangeMax}`;
    }
    if (col.lengthMin != null && col.lengthMax != null) {
        const avg = col.lengthAvg != null ? ` (avg: ${col.lengthAvg})` : "";
        return `${col.lengthMin}-${col.lengthMax}${avg}`;
    }
    return "-";
};

const isAllSingletons = (column) => column.distinctPercent != null && Number(column.distinctPercent) === 100;
const isOutlierFocus = (column) => column.distinctPercent != null && Number(column.distinctPercent) >= 90 && Number(column.distinctPercent) < 100;

const UniqueDataSummary = ({ column }) => {
    const sampleValue = column.mostPopularValue;
    const topPattern = column.formatPatterns && column.formatPatterns.length > 0 ? column.formatPatterns[0] : null;

    return (
        <div style={{ marginTop: 4, padding: "8px 10px", background: "#f0f7ff", borderRadius: 4, border: "1px solid #d6e8fa" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 12 }}>⭐</span>
                <Tag color="blue" style={{ fontSize: 11, fontWeight: 700, margin: 0 }}>Unique</Tag>
                <span style={{ fontSize: 11, color: "#555" }}>
                    All values are distinct ({column.distinctCount != null ? Number(column.distinctCount).toLocaleString() : "–"} values)
                </span>
            </div>
            <div style={{ display: "flex", gap: 20, fontSize: 11, color: "#555" }}>
                {column.lengthMin != null && column.lengthMax != null && (
                    <span><strong>Length:</strong> {column.lengthMin}–{column.lengthMax}{column.lengthAvg != null ? ` (avg: ${column.lengthAvg})` : ""}</span>
                )}
                {column.rangeMin != null && column.rangeMax != null && (
                    <span><strong>Range:</strong> {column.rangeMin}–{column.rangeMax}</span>
                )}
                {sampleValue && (
                    <span><strong>Sample:</strong> {sampleValue}</span>
                )}
                {topPattern && (
                    <span><strong>Format:</strong> {topPattern.pattern} ({topPattern.pct != null ? `${topPattern.pct}%` : `${topPattern.percentage}%`})</span>
                )}
            </div>
        </div>
    );
};

const ColumnProfilingCard = ({ column, onViewAll }) => {
    const isSkipped = column.skipped;
    const isAllNull = column.nullCount != null && column.totalRecords != null && column.nullCount === column.totalRecords;
    const isSingleton = isAllSingletons(column);

    return (
        <Card
            size="small"
            style={{ marginBottom: 8, borderColor: "#e8e8e8" }}
            bodyStyle={{ padding: "10px 14px" }}
        >
            {/* Header row */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                <div>
                    <span style={{ fontWeight: 600, fontSize: 14, color: "#1890ff" }}>{column.fieldName || column.name}</span>
                    <div style={{ marginTop: 3, display: "flex", gap: 4 }}>
                        {column.sqlType && (
                            <Tag style={{ fontSize: 10, fontWeight: 700, margin: 0 }}>
                                {column.sqlType.charAt(0).toUpperCase() + column.sqlType.slice(1)}
                            </Tag>
                        )}
                        {column.fieldDescription && (
                            <Tag color="blue" style={{ fontSize: 10, fontWeight: 700, margin: 0 }}>
                                {column.fieldDescription}
                            </Tag>
                        )}
                    </div>
                </div>
                <div style={{ display: "flex", gap: 20, fontSize: 11 }}>
                    <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 10, color: "#444" }}>Distinct</div>
                        <div style={{ fontWeight: 700, color: "#1a3e5c" }}>{column.distinctCount != null ? Number(column.distinctCount).toLocaleString() : "-"}{column.distinctCapped ? " ~" : ""}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 10, color: "#444" }}>NULL</div>
                        <div style={{ fontWeight: 700, color: "#d9534f" }}>
                            {column.nullPercent != null ? `${Number(column.nullPercent).toFixed(1)}%` : "-"}
                        </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 10, color: "#444" }}>Blank</div>
                        <div style={{ fontWeight: 700, color: "#fa8c16" }}>
                            {column.blankPercent != null ? `${Number(column.blankPercent).toFixed(1)}%` : "-"}
                        </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 10, color: "#444" }}>Complete</div>
                        <div style={{ fontWeight: 700, color: "#5cb3a3" }}>
                            {column.completePercent != null ? `${Number(column.completePercent).toFixed(1)}%` : "-"}
                        </div>
                    </div>
                </div>
            </div>

            {/* Edge case labels */}
            {isSkipped && (
                <div style={{ fontSize: 11, color: "#999", fontStyle: "italic", margin: "4px 0" }}>Field skipped (binary or unsupported type)</div>
            )}
            {isAllNull && !isSkipped && (
                <div style={{ fontSize: 11, color: "#fa8c16", fontStyle: "italic", margin: "4px 0" }}>All values null</div>
            )}

            {/* Singleton: Unique Data Summary instead of distribution chart */}
            {!isSkipped && !isAllNull && isSingleton && (
                <UniqueDataSummary column={column} />
            )}

            {/* Normal: Value Distribution Chart */}
            {!isSkipped && !isAllNull && !isSingleton && column.valueDistribution && column.valueDistribution.length > 0 && (
                <div style={{ marginTop: 4 }}>
                    <div style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
                        <div style={{ fontSize: 11, color: "#555", fontWeight: 600 }}>Value Distribution</div>
                        {column.canViewAll && (
                            <Button
                                type="link"
                                size="small"
                                onClick={() => onViewAll(column)}
                                style={{ marginLeft: "auto", padding: 0, height: 18, fontSize: 11, fontWeight: 600 }}
                            >
                                View All
                            </Button>
                        )}
                    </div>
                    <ProfilingBarChart data={column.valueDistribution} />
                </div>
            )}

            {/* Footer stats */}
            {!isSkipped && !isAllNull && (
                <div style={{ display: "flex", gap: 24, marginTop: 8, fontSize: 11, color: "#555" }}>
                    {isSingleton ? null : isOutlierFocus(column) ? (
                        <span>
                            <strong>Near-Unique:</strong>{" "}
                            {Number(column.distinctCount).toLocaleString()} Unique,{" "}
                            {((column.totalRecords || 0) - Number(column.distinctCount)).toLocaleString()} Duplicate
                            {column.mostPopularValue ? <span style={{ color: "#888" }}> ({column.mostPopularValue})</span> : ""}
                        </span>
                    ) : column.mostPopularValue ? (
                        <span>
                            <strong>Most Popular:</strong> {column.mostPopularValue}
                            {column.mostPopularFrequency != null && ` (${Number(column.mostPopularFrequency).toLocaleString()})`}
                        </span>
                    ) : null}
                    {!isSingleton && formatRangeOrLength(column) !== "-" && (
                        <span><strong>{column.rangeMin != null ? "Range:" : "Length:"}</strong> {formatRangeOrLength(column)}</span>
                    )}
                    {column.approximateMedian != null && (
                        <span>Median: ≈{column.approximateMedian}</span>
                    )}
                </div>
            )}
        </Card>
    );
};

const ProfilingChartView = ({ columns, onViewAll }) => {
    if (!columns || columns.length === 0) {
        return <Empty description="No profiling data available" />;
    }

    return (
        <div style={{ overflowY: "auto", flex: 1 }}>
            {columns.map((col, idx) => (
                <ColumnProfilingCard key={col.fieldName || col.name || idx} column={col} onViewAll={onViewAll} />
            ))}
        </div>
    );
};

const ProfilingTableView = ({ columns }) => {
    if (!columns || columns.length === 0) {
        return <Empty description="No profiling data available" />;
    }

    const tableColumns = [
        {
            title: "Column",
            dataIndex: "fieldName",
            key: "fieldName",
            width: 140,
            render: (value) => <span style={{ color: "#1890ff", fontWeight: 500 }}>{value || "-"}</span>,
        },
        {
            title: "SQL Type",
            dataIndex: "sqlType",
            key: "sqlType",
            width: 80,
            render: (value) => value ? <Tag style={{ fontSize: 10, margin: 0 }}>{value.charAt(0).toUpperCase() + value.slice(1)}</Tag> : "-",
        },
        {
            title: "Inferred",
            dataIndex: "inferredType",
            key: "inferredType",
            width: 90,
            render: (value) => value ? <Tag color={getInferredTypeColor(value)} style={{ fontSize: 10, margin: 0 }}>{value}</Tag> : "-",
        },
        {
            title: "Distinct",
            dataIndex: "distinctCount",
            key: "distinctCount",
            width: 70,
            render: (value, record) => {
                const display = value != null ? Number(value).toLocaleString() : "-";
                return record.distinctCapped ? <Tooltip title="Capped estimate">~{display}</Tooltip> : display;
            },
        },
        {
            title: "Distinct %",
            dataIndex: "distinctPercent",
            key: "distinctPercent",
            width: 70,
            render: (value) => value != null ? `${Number(value).toFixed(1)}%` : "-",
        },
        {
            title: "NULL #",
            dataIndex: "nullCount",
            key: "nullCount",
            width: 60,
            render: (value) => value != null ? Number(value).toLocaleString() : "-",
        },
        {
            title: "NULL %",
            dataIndex: "nullPercent",
            key: "nullPercent",
            width: 60,
            render: (value) => {
                if (value == null) return "-";
                const style = value > 0 ? { color: "#fa541d" } : {};
                return <span style={style}>{Number(value).toFixed(1)}%</span>;
            },
        },
        {
            title: "Blank #",
            dataIndex: "blankCount",
            key: "blankCount",
            width: 60,
            render: (value) => value != null ? Number(value).toLocaleString() : "-",
        },
        {
            title: "Blank %",
            dataIndex: "blankPercent",
            key: "blankPercent",
            width: 60,
            render: (value) => value != null ? `${Number(value).toFixed(1)}%` : "-",
        },
        {
            title: "Complete %",
            dataIndex: "completePercent",
            key: "completePercent",
            width: 75,
            render: (value) => {
                if (value == null) return "-";
                const style = value < 100 ? { color: "#fa541d" } : { color: "#52c41a" };
                return <span style={style}>{Number(value).toFixed(1)}%</span>;
            },
        },
        {
            title: "Most Popular",
            dataIndex: "mostPopularValue",
            key: "mostPopularValue",
            width: 110,
            ellipsis: { showTitle: false },
            render: (value, record) => {
                if (isAllSingletons(record)) {
                    return (
                        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <span style={{ fontSize: 12 }}>⭐</span>
                            <Tag color="blue" style={{ fontSize: 10, fontWeight: 700, margin: 0 }}>Unique</Tag>
                            <span style={{ fontSize: 9, color: "#888" }}>(All values are distinct)</span>
                        </span>
                    );
                }
                if (isOutlierFocus(record)) {
                    const totalRecords = record.totalRecords || 0;
                    const distinctCount = record.distinctCount != null ? Number(record.distinctCount) : 0;
                    const duplicateCount = totalRecords - distinctCount;
                    return (
                        <Tooltip title={value}>
                            <span>
                                {distinctCount.toLocaleString()} Unique, {duplicateCount.toLocaleString()} Duplicate
                                {value ? <span style={{ color: "#888" }}> ({value})</span> : ""}
                            </span>
                        </Tooltip>
                    );
                }
                return (
                    <Tooltip title={value}>
                        {value || "-"}
                        {record.mostPopularFrequency != null && value ? ` (${Number(record.mostPopularFrequency).toLocaleString()})` : ""}
                    </Tooltip>
                );
            },
        },
        {
            title: "Range/Length",
            key: "rangeLength",
            width: 120,
            render: (_, record) => formatRangeOrLength(record),
        },
    ];

    return (
        <div style={{ overflowY: "auto", flex: 1 }}>
            <Table
                size="small"
                rowKey={(record) => record.fieldId || record.fieldName || record.name}
                dataSource={columns}
                columns={tableColumns}
                pagination={false}
                scroll={{ x: "max-content" }}
            />
        </div>
    );
};

const ProfilingStatistics = ({ profilingData, viewMode }) => {
    const [expandedColumn, setExpandedColumn] = useState(null);

    if (!profilingData || !profilingData.columns || profilingData.columns.length === 0) {
        return <Empty description="No profiling data. Click 'Run Profiling' to generate statistics." style={{ marginTop: 24 }} />;
    }

    return (
        <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "auto" }}>
            <SummaryCards
                totalRecords={profilingData.totalRecords}
                totalColumns={profilingData.totalColumns}
                lastUpdated={profilingData.lastUpdated}
            />
            {viewMode === "chart" ? (
                <ProfilingChartView columns={profilingData.columns} onViewAll={setExpandedColumn} />
            ) : (
                <ProfilingTableView columns={profilingData.columns} totalRecords={profilingData.totalRecords} />
            )}
            <Modal
                open={Boolean(expandedColumn)}
                onCancel={() => setExpandedColumn(null)}
                footer={null}
                width={900}
                title={`Value Distribution - ${expandedColumn?.fieldName || expandedColumn?.name || "Column"}`}
                styles={{ body: { maxHeight: "70vh", overflowY: "auto" } }}
            >
                {expandedColumn?.distributionApproximate && (
                    <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
                        Counts are approximate for high-cardinality fields.
                    </div>
                )}
                <ProfilingBarChart data={expandedColumn?.expandedValueDistribution || []} />
            </Modal>
        </div>
    );
};

export default ProfilingStatistics;
